import { defineStore } from 'pinia';
import { ref } from 'vue';
import { getSafeAxiosClient } from '@/helpers/axiosProxy';
import { useNotificationsStore } from '@/store/notificationsStore';

export interface CounterData {
  name: string;
  count: number;
  description?: string;
}

export interface ActivityResult {
  hasError: boolean;
  isSuccess: boolean;
  taskId: string;
  taskName: string;
  message: string | null;
  errors: string[];
  duration: number;
  status: string;
  continuationToken: string;
  data: CounterData[] | null;
}

export interface ProcessResult {
  isComplete: boolean;
  hasError: boolean;
  activityResults: ActivityResult[];
  totalTasks: number;
  errorTasks: number;
  errorMessages: string[];
  duration: number;
  data: CounterData[] | null;
  hasAggregateError: boolean;
  startTime: string;
  endTime: string;
  processType: string;
  instanceId: string;
  fileName: string;
  dbTableName: string | null;
  dbSchemaName: string | null;
  dbName: string;
  blobName: string;
  userName: string;
}

export interface ImportLogEntry {
  importLogId: number;
  importId: string;
  uploadId: string;
  isComplete: boolean;
  hasError: boolean;
  stagingRowCount: number | null;
  targetRowCount: number | null;
  readRowCount: number | null;
  result: ProcessResult | null;
  finished: string | null;
  createdAt: string;
}

export const useImportLogStore = defineStore('importLogStore', () => {
  const importLog = ref<ImportLogEntry[] | null>(null);
  const isLoading = ref(false);
  const importLogDetails = ref<any>(null);
  const isLoadingDetails = ref(false);
  const notificationsStore = useNotificationsStore();

  // Henter import-log fra store (med fetch som backing hvis cache er tom/utdatert)
  async function getImportLog (uploadId: string) {
    // Returner fra cache hvis data allerede er hentet for samme uploadId
    if (importLog.value && importLog.value.length > 0 && importLog.value[0].uploadId === uploadId) {
      return;
    }
    isLoading.value = true;
    try {
      const axiosClient = await getSafeAxiosClient();
      const response = await axiosClient.get<ImportLogEntry[]>(`v2/upload/import-log/${uploadId}`);
      importLog.value = response.data;
    } catch (error) {
      importLog.value = null;
      console.error('Feil ved henting av import log:', error);
      notificationsStore.addError('Kunne ikke hente import log');
    } finally {
      isLoading.value = false;
    }
  }

  async function getImportLogDetails (uploadId: string, resultset: string, tableDefinitionId?: number | null) {
    isLoadingDetails.value = true;
    try {
      const axiosClient = await getSafeAxiosClient();

      // Først hent import-log for å få importId
      const importLogResponse = await axiosClient.get<ImportLogEntry[]>(`v2/upload/import-log/${uploadId}`);
      const importLog = importLogResponse.data;

      if (!importLog || importLog.length === 0) {
        throw new Error('Ingen import-log funnet for uploadId');
      }

      // Bruk den nyeste importId (første i listen)
      const importId = importLog[0].importId;

      if (!importId) {
        throw new Error(`ImportId er undefined for uploadId ${uploadId}`);
      }

      const params = { tableDefinitionId }
      const url = `v2/upload/import-log/${importId}/${resultset}`;
      const response = await axiosClient.get(url, { params });
      importLogDetails.value = response.data;
      return response.data;
    } catch (error) {
      console.error('Feil ved henting av import log detaljer:', error);
      notificationsStore.addError('Kunne ikke hente import log detaljer');
      throw error;
    } finally {
      isLoadingDetails.value = false;
    }
  }

  async function fetchImportLogByImportId (importId: string) {
    isLoading.value = true;
    try {
      const axiosClient = await getSafeAxiosClient();
      const response = await axiosClient.get(`v2/upload/import-log-by-id/${importId}`);
      return response.data;
    } catch (error) {
      notificationsStore.addError('Kunne ikke hente import-logg for importId');
      throw error;
    } finally {
      isLoading.value = false;
    }
  }

  return {
    importLog,
    isLoading,
    importLogDetails,
    isLoadingDetails,
    getImportLog,
    getImportLogDetails,
    fetchImportLogByImportId,
  };
});
